import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, Button, CssBaseline, MenuItem, Paper, Select, Typography } from '@mui/material';
import EventSeatIcon from '@mui/icons-material/EventSeat';
import PeopleIcon from '@mui/icons-material/People';
import HistoryIcon from '@mui/icons-material/History';
import DownloadIcon from '@mui/icons-material/Download';
import LogoutIcon from '@mui/icons-material/Logout';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { AppShell, LoadingState, StatusChip, useToast } from '../../ui';
import {
  IT_ADMIN_LOGIN_PATH,
  LicApiContext,
  SUPER_ADMIN_LOGIN_PATH,
  createLicApi,
  getAdminToken,
  getLicSession,
  getMe,
  orgPath,
  signOut,
} from './licApi';
import LicensesView from './LicensesView';
import PeopleView from './PeopleView';
import ActivityView from './ActivityView';
import DownloadsView from './DownloadsView';

const ALL_VIEWS = [
  { label: 'Licenses', value: 'licenses', icon: <EventSeatIcon fontSize="small" /> },
  { label: 'People', value: 'people', icon: <PeopleIcon fontSize="small" /> },
  { label: 'Activity', value: 'activity', icon: <HistoryIcon fontSize="small" /> },
  { label: 'Downloads', value: 'downloads', icon: <DownloadIcon fontSize="small" /> },
];

const SELECTED_ORG_KEY = 'orgLicSelectedOrg';

const readSelectedOrg = () => {
  try {
    return localStorage.getItem(SELECTED_ORG_KEY) || '';
  } catch (error) {
    return '';
  }
};

const rememberSelectedOrg = (orgId) => {
  try {
    localStorage.setItem(SELECTED_ORG_KEY, orgId);
  } catch (error) {
    // Only a convenience.
  }
};

const pickOrg = (orgs, preferred) => {
  if (!orgs.length) {
    return '';
  }
  return orgs.some((org) => org.id === preferred) ? preferred : orgs[0].id;
};

/**
 * Org licensing dashboard for an org's IT admins (web session, /org-licensing) and, with `superAdmin`, for
 * Vavetek admins opening any org from /admin/org-licensing/:orgId (platform admin JWT).
 */
const OrgLicensingDashboard = ({ superAdmin = false }) => {
  const navigate = useNavigate();
  const { orgId: routeOrgId } = useParams();
  const api = useMemo(() => createLicApi({ superAdmin }), [superAdmin]);
  const initialSession = useMemo(() => (superAdmin ? null : getLicSession()), [superAdmin]);
  const [adminOrgs, setAdminOrgs] = useState(initialSession?.adminOrgs || []);
  const [email, setEmail] = useState(initialSession?.email || '');
  const [selectedOrgId, setSelectedOrgId] = useState(() => pickOrg(initialSession?.adminOrgs || [], readSelectedOrg()));
  const orgId = superAdmin ? routeOrgId || '' : selectedOrgId;
  const [org, setOrg] = useState(null);
  const [role, setRole] = useState(null);
  const [view, setView] = useState('licenses');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const notify = useToast();

  // Make sure we are signed in, and refresh the list of orgs this person administers.
  useEffect(() => {
    if (superAdmin) {
      if (!getAdminToken()) {
        navigate(SUPER_ADMIN_LOGIN_PATH, { replace: true });
      } else if (!routeOrgId) {
        setError('No organization was selected.');
        setLoading(false);
      }
      return undefined;
    }
    if (!getLicSession()) {
      navigate(IT_ADMIN_LOGIN_PATH, { replace: true });
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const me = await getMe(api);
        if (cancelled) return;
        if (me.email) setEmail(me.email);
        setAdminOrgs(me.adminOrgs);
        setSelectedOrgId((current) => pickOrg(me.adminOrgs, current || readSelectedOrg()));
        if (!me.adminOrgs.length) {
          setError("This email isn't an IT admin for any organization.");
          setLoading(false);
        }
      } catch (loadError) {
        // Keep using the orgs saved at sign-in if /me fails for another reason.
        if (!cancelled && loadError.status !== 401 && !(initialSession?.adminOrgs || []).length) {
          setError(loadError.message);
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, navigate, superAdmin, routeOrgId, initialSession]);

  // Load the selected org (never show one org's data while requests target another).
  useEffect(() => {
    if (!orgId) return undefined;
    let cancelled = false;
    setOrg(null);
    setError('');
    setLoading(true);
    (async () => {
      try {
        const result = await api.licFetch(orgPath(orgId));
        if (cancelled) return;
        setOrg(result.org || null);
        setRole(result.role || null);
        if (!result.org) setError('This organization could not be loaded.');
        setLoading(false);
      } catch (loadError) {
        if (!cancelled && loadError.status !== 401) {
          setError(loadError.status === 403 ? 'You are not an admin of this organization.' : loadError.message);
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, orgId]);

  const reloadOrg = useCallback(async () => {
    if (!orgId) return;
    try {
      const result = await api.licFetch(orgPath(orgId));
      if (result.org) setOrg(result.org);
    } catch (loadError) {
      // The view that triggered the reload already reported its own result.
    }
  }, [api, orgId]);

  const handleExit = async () => {
    if (superAdmin) {
      navigate('/admin/dashboard', { state: { view: 'orgLicensing' } });
      return;
    }
    await signOut();
    navigate(IT_ADMIN_LOGIN_PATH, { replace: true });
  };

  const handleOrgChange = (nextOrgId) => {
    rememberSelectedOrg(nextOrgId);
    setSelectedOrgId(nextOrgId);
    setView('licenses');
  };

  const isPersonal = org?.type === 'Personal';
  // A Personal license has no org code, domains or members to manage.
  const options = isPersonal ? ALL_VIEWS.filter((option) => option.value !== 'people') : ALL_VIEWS;
  const exitLabel = superAdmin ? 'Back to admin' : 'Sign out';


  if (loading) {
    return <LoadingState fullPage />;
  }

  if (error || !org) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: 'background.default', p: 2 }}>
        <CssBaseline />
        <Paper variant="outlined" sx={{ p: 4, maxWidth: 520, width: '100%', textAlign: 'left' }}>
          <Typography variant="h5" component="h1" sx={{ mb: 2 }}>
            Licenses
          </Typography>
          <Alert severity="warning" sx={{ mb: 3 }}>{error || 'This organization could not be loaded.'}</Alert>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {!superAdmin && adminOrgs.length > 1 && (
              <Select size="small" value={orgId} onChange={(event) => handleOrgChange(event.target.value)}>
                {adminOrgs.map((item) => (
                  <MenuItem key={item.id} value={item.id}>{item.name || item.id}</MenuItem>
                ))}
              </Select>
            )}
            <Button variant="contained" onClick={handleExit}>
              {exitLabel}
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }

  const currentView = options.some((option) => option.value === view) ? view : 'licenses';

  const renderContent = () => {
    switch (currentView) {
      case 'people':
        return <PeopleView key={org.id} org={org} myEmail={email} notify={notify} onOrgChanged={reloadOrg} />;
      case 'activity':
        return <ActivityView key={org.id} org={org} />;
      case 'downloads':
        return <DownloadsView key={org.id} org={org} notify={notify} />;
      case 'licenses':
      default:
        return <LicensesView key={org.id} org={org} notify={notify} onOrgChanged={reloadOrg} />;
    }
  };

  // Org name (or a picker when this person runs several orgs), shown in the top bar.
  const orgTitle = !superAdmin && adminOrgs.length > 1 ? (
    <Select
      id="org-lic-org"
      size="small"
      value={orgId}
      onChange={(event) => handleOrgChange(event.target.value)}
      sx={{
        maxWidth: 280,
        color: '#FFFFFF',
        fontWeight: 600,
        backgroundColor: 'rgba(255,255,255,0.08)',
        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.25)' },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.5)' },
        '& .MuiSvgIcon-root': { color: '#FFFFFF' },
      }}
    >
      {adminOrgs.map((item) => (
        <MenuItem key={item.id} value={item.id}>{item.name || item.id}</MenuItem>
      ))}
    </Select>
  ) : (
    <Typography noWrap sx={{ color: '#FFFFFF', fontWeight: 600, fontSize: 15, minWidth: 0 }}>
      {org.name || org.ownerEmail || 'Organization'}
    </Typography>
  );

  return (
    <LicApiContext.Provider value={api}>
      <AppShell
        area="Licenses"
        homeTo={superAdmin ? '/admin/dashboard' : '/org-licensing'}
        nav={options}
        selected={currentView}
        onSelect={setView}
        topBarContent={
          <>
            <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 1, minWidth: 0 }}>
              <Typography sx={{ color: 'rgba(255,255,255,0.4)' }}>/</Typography>
              {orgTitle}
            </Box>
            <StatusChip label={org.edition || 'Online'} tone="brand" />
            {isPersonal && <StatusChip label="Personal" tone="brand" />}
            {superAdmin && (
              <StatusChip label={role === 'VavetekAdmin' || !role ? 'Vavetek admin' : role} tone="brand" sx={{ display: { xs: 'none', md: 'flex' } }} />
            )}
          </>
        }
        account={{
          name: superAdmin ? 'Vavetek admin' : undefined,
          email,
          items: [
            superAdmin
              ? { label: exitLabel, onClick: handleExit, icon: <ArrowBackIcon fontSize="small" /> }
              : { label: exitLabel, onClick: handleExit, icon: <LogoutIcon fontSize="small" /> },
          ],
        }}
      >
        {/* On phones the org name (or picker) is not in the top bar, so show it above the page. */}
        <Box sx={{ display: { xs: 'flex', sm: 'none' }, mb: 2 }}>
          {!superAdmin && adminOrgs.length > 1 ? (
            <Select size="small" fullWidth value={orgId} onChange={(event) => handleOrgChange(event.target.value)}>
              {adminOrgs.map((item) => (
                <MenuItem key={item.id} value={item.id}>{item.name || item.id}</MenuItem>
              ))}
            </Select>
          ) : (
            <Typography sx={{ fontWeight: 600, color: 'text.secondary' }}>{org.name || org.ownerEmail}</Typography>
          )}
        </Box>
        {renderContent()}
      </AppShell>
    </LicApiContext.Provider>
  );
};

export default OrgLicensingDashboard;
